import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from "@/components/ui/chart";
import { INSIGHT_CHART_MARGIN } from "@/lib/chartLayout";
import { buildMonthlyCashFlow } from "@/lib/analytics";
import { Transaction } from "@/lib/types";
import { BadgeDollarSign } from "lucide-react";
// [T149-PR1] Adopt chart cell wrapper.
import { RechartsCell } from "@/components/dashboard/widgets/primitives/RechartsCell";

interface CashFlowChartProps {
  transactions: Transaction[];
}

const chartConfig: ChartConfig = {
  income: {
    label: "Income",
    color: "hsl(var(--chart-1))",
  },
  expenses: {
    label: "Expenses",
    color: "hsl(var(--chart-2))",
  },
  netCashFlow: {
    label: "Net Cash Flow",
    color: "hsl(var(--chart-3))",
  },
};

export function CashFlowChart({ transactions }: CashFlowChartProps) {
  const data = useMemo(() => buildMonthlyCashFlow(transactions, 6), [transactions]);

  return (
    <Card
      data-testid="home-card-chart-cashflow"
      className="glass-panel h-full transition-all duration-200 hover:shadow-md hover:border-primary/20 min-w-0"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-full shrink-0">
            <BadgeDollarSign className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <CardTitle className="truncate">Cash Flow (Last 6 Months)</CardTitle>
            <p className="text-sm text-muted-foreground truncate">Income vs expenses with net trend</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-y-auto px-2 sm:px-6">
        {data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No transaction data yet. Add income and expenses to see cash flow.</p>
      ) : (
        <RechartsCell minHeight={250}>
          <ChartContainer config={chartConfig} className="h-[250px] md:h-[320px] w-full">
            <ComposedChart data={data} margin={INSIGHT_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                className="text-xs text-muted-foreground"
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(value) => `$${value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}`}
                tickLine={false}
                axisLine={false}
                width={36}
                className="text-xs text-muted-foreground"
                tick={{ fontSize: 10 }}
              />
              <ChartTooltip
                content={<ChartTooltipContent labelKey="month" />}
                cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                dataKey="income"
                stackId="cash"
                radius={[6, 6, 0, 0]}
                fill="var(--color-income)"
                maxBarSize={36}
              />
              <Bar
                dataKey="expenses"
                stackId="cash"
                radius={[6, 6, 0, 0]}
                fill="var(--color-expenses)"
                maxBarSize={36}
              />
              <Line
                type="monotone"
                dataKey="netCashFlow"
                stroke="var(--color-netCashFlow)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "var(--color-netCashFlow)" }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ChartContainer>
        </RechartsCell>
      )}
      </CardContent>
    </Card>
  );
}
